import React from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/use-language";
import { Facebook, Instagram } from "lucide-react";
import LandingPageContent from "@/components/landing/LandingPageContent";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { withAuthRoutePrefix } from "@/config/auth";

export default function LandingPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login, logout } = useAuth();
  const dashboardPath = withAuthRoutePrefix("/", location.pathname);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-6 py-24">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">NTNUi Padel</h1>
            <p className="text-xl md:text-2xl mb-8 text-green-100">
              Lyst til å spille padel tennis? / Want to play padel tennis?
            </p>
            <p className="text-lg mb-12 max-w-2xl mx-auto leading-relaxed">
              Drop-in trening 72 timer i forveien. Bli med på våre økter! / Drop-in training - register 72 hours in
              advance. Join our sessions!
            </p>
            {!isAuthenticated ? (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-white text-green-600 hover:bg-green-50 font-semibold px-8 py-3"
                  onClick={() => login("", false)}
                >
                  {t("landing.hero.join") || "Bli medlem / Become Member"}
                </Button>
                <Button
                  size="lg"
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-green-600 font-semibold px-8 py-3 transition-colors"
                  onClick={() => login("", false)}
                >
                  {t("landing.hero.signin") || "Logg inn / Sign In"}
                </Button>

              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  size="lg"
                  className="bg-white text-green-600 hover:bg-green-50 font-semibold px-8 py-3"
                  onClick={() => navigate(dashboardPath)}
                >
                  {t("landing.hero.dashboard") || "Go to dashboard"}
                </Button>
                <Button variant="outline" onClick={() => void logout()}>
                  {t("auth.logout") || "Sign out"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16 space-y-16">
        <LandingPageContent />

        {/* Call to Action */}
        <section className="text-center bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-2xl p-12">
          <h2 className="text-3xl font-bold mb-4">Klar til å spille? / Ready to Play?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Bli medlem og kjøp drop-in medlemskap for å spille med oss! / Become a member and purchase drop-in
            membership to play with us!
          </p>
          {!isAuthenticated ? (
            <Button
              size="lg"
              className="bg-white text-green-600 hover:bg-green-50 font-semibold px-8 py-3"
              onClick={() => login("", false)}
            >
              Kom i gang i dag / Get Started Today
            </Button>
          ) : (
            <Button
              size="lg"
              variant="outline"
              className="text-white border-white hover:bg-white hover:text-green-600 font-semibold px-8 py-3"
              onClick={() => navigate(dashboardPath)}
            >
              {t("landing.hero.dashboard") || "Go to dashboard"}
            </Button>
          )}
        </section>

        {/* Social Links */}
        <section className="text-center">
          <h3 className="text-2xl font-bold mb-6">Follow Us</h3>
          <div className="flex justify-center gap-6">
            <a
              href="https://www.facebook.com/NTNUiPadel"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <Facebook className="h-6 w-6" />
              <span className="font-medium">Facebook</span>
            </a>
            <a
              href="https://www.instagram.com/NTNUiPadel"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-pink-600 hover:text-pink-800 transition-colors"
            >
              <Instagram className="h-6 w-6" />
              <span className="font-medium">Instagram</span>
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
